// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Compte utilisateur — connexion, double authentification, profil, préférences
// et page Paramètres.
//
// ⚠️ `signup_closed_*` n'est pas du texte mort : les inscriptions self-service
// sont FERMÉES depuis le 27/07/2026 (drapeau serveur `SIGNUP_ENABLED`, défaut
// `false`). Ces libellés sont ce que voit un visiteur aujourd'hui. Les retirer
// suppose de rouvrir les inscriptions d'abord.
//
// ⚠️ `login_mfa_setup_required_*` sert un cas précis : tout compte `admin`/`dev`
// sans MFA configuré reçoit `mfa_setup_required` au login, sans jeton d'accès.

const compte = {
  fr: {
    // ── Connexion ──
    login_no_account_prompt:        'Pas encore de compte ?',
    login_signup_link:              'Créer un compte',
    login_mfa_verify_title:         'Vérification en deux étapes',
    login_mfa_verify_intro:         "Saisissez le code affiché dans votre application d'authentification.",
    login_mfa_recovery_hint:        'Vous pouvez aussi saisir un code de récupération.',
    login_mfa_back_to_login:        'Retour à la connexion',
    login_mfa_setup_required_title: 'Sécurité renforcée requise',
    login_mfa_setup_required_intro: "Votre rôle nécessite l'activation de l'authentification à deux facteurs avant de continuer.",

    // ── Acceptation des CGU/CGV (24 septembre 2026) ──
    // ⚠️ `{v}` et `{date}` sont substitués dans AcceptationCgv.js.
    cgv_acc_titre:            "Conditions générales d'utilisation et de vente",
    cgv_acc_intro:            'Pour utiliser Normascan, acceptez la version {v} de nos CGU/CGV, en vigueur depuis le {date}.',
    cgv_acc_intro_a_venir:    "La version {v}, applicable à partir du {date}, vous est présentée dès maintenant : en acceptant, vous acceptez l'une et l'autre.",
    cgv_acc_ce_qui_change:    'Ce qui change :',
    cgv_acc_lire_cgv:         'Lire les CGU/CGV',
    cgv_acc_lire_privacy:     'Lire la politique de confidentialité',
    cgv_acc_aide_decision:    "Normascan est un outil d'aide à la décision : ses résultats doivent être validés par un professionnel certifié.",
    cgv_acc_case:             "J'ai lu et j'accepte les CGU/CGV et la politique de confidentialité",
    cgv_acc_accepter:         'Accepter et continuer',
    cgv_acc_en_cours:         'Validation…',
    cgv_acc_refuser_resilier: 'Je refuse et je résilie mon abonnement',
    cgv_acc_refuser:          'Je refuse',
    cgv_acc_refus_titre:      "Vous n'avez pas accepté les conditions générales",
    cgv_acc_refus_siege:      "Sans cette acceptation, vous ne pouvez pas utiliser Normascan. Rapprochez-vous de l'administrateur de votre licence.",
    cgv_acc_refus_individuel: 'Sans cette acceptation, vous ne pouvez pas utiliser Normascan. Pour toute question : contact@evalens.fr.',
    cgv_acc_erreur_portail:   "La page de gestion de l'abonnement n'a pas pu s'ouvrir. Pour résilier, écrivez-nous à contact@evalens.fr.",
    cgv_acc_deconnecter:      'Se déconnecter',
    // Bandeau de la page /cgv (préavis, lecture d'une autre version).
    cgv_page_a_venir:          "Une nouvelle version ({v}) de ces conditions s'appliquera à partir du {date}.",
    cgv_page_lire:             'Lire la version {v}',
    cgv_page_lecture_a_venir:  "Vous lisez la version {v}, qui s'appliquera à partir du {date}.",
    cgv_page_lecture_ancienne: "Vous lisez la version {v}, entrée en vigueur le {date}, qui n'est plus celle en vigueur.",
    cgv_page_lire_en_vigueur:  'Lire la version en vigueur ({v})',
    cgv_page_hors_ligne:       "La version {v} n'est plus affichée en ligne ; ci-dessous, la version en vigueur.",
    cgv_page_telecharger_pdf:  'Télécharger la version {v} (PDF)',
    cgv_page_sans_pdf:         "La version {v} n'est pas disponible en ligne ; ci-dessous, la version en vigueur. Pour l'obtenir : contact@evalens.fr.",
    cgv_page_inconnue:         "Il n'existe pas de version {v} de ces conditions ; ci-dessous, la version en vigueur.",
    cgv_page_archive_titre:    'Versions en PDF',
    cgv_page_archive_ligne:    'Version {v}, en vigueur à partir du {date} (PDF)',

    // ── Mot de passe oublié (29 juillet 2026) ──
    pwd_forgot_link:                'Mot de passe oublié ?',
    pwd_forgot_title:               'Réinitialiser votre mot de passe',
    pwd_forgot_intro:               'Saisissez votre adresse email. Si un compte lui est associé, vous recevrez un lien pour choisir un nouveau mot de passe.',
    pwd_forgot_email_label:         'Adresse email',
    pwd_forgot_submit:              'Envoyer le lien',
    pwd_forgot_sending:             'Envoi en cours…',
    pwd_forgot_sent_title:          'Vérifiez votre boîte mail',
    pwd_forgot_sent_body:           "Si un compte existe pour cette adresse, un lien de réinitialisation vient d'être envoyé. Il est valable une heure et ne peut servir qu'une fois.",
    pwd_back_to_login:              'Retour à la connexion',
    pwd_reset_title:                'Choisissez un nouveau mot de passe',
    pwd_reset_password_label:       'Nouveau mot de passe',
    pwd_reset_confirm_label:        'Confirmer le mot de passe',
    pwd_reset_submit:               'Enregistrer le nouveau mot de passe',
    pwd_reset_submitting:           'Enregistrement…',
    pwd_reset_done_title:           'Mot de passe modifié',
    pwd_reset_done_body:            'Votre mot de passe a été mis à jour. Vous pouvez maintenant vous connecter.',
    pwd_rules:                      'Au moins 8 caractères, une majuscule et un chiffre.',
    pwd_mismatch:                   'Les mots de passe ne correspondent pas',
    pwd_error_generic:              'Une erreur est survenue, veuillez réessayer',

    // ── Inscription fermée (drapeau SIGNUP_ENABLED) ──
    signup_closed_contact:          "Les inscriptions en ligne sont temporairement fermées. Contactez-nous pour ouvrir un accès.",
    signup_closed_title:            'Inscriptions temporairement fermées',
    signup_closed_body:             "La création de compte en ligne n'est pas disponible pour le moment. Contactez-nous pour ouvrir un accès à votre équipe.",
    signup_closed_login:            'J’ai déjà un compte',
    signup_closed_contact_cta:      'Nous contacter',

    // ── Double authentification ──
    mfa_section_title:              'Authentification à deux facteurs',
    mfa_status_enabled:             'Activé',
    mfa_status_disabled:            'Désactivé',
    mfa_enable_cta:                 'Activer',
    mfa_disable_cta:                'Désactiver',
    mfa_setup_intro:                "Scannez ce QR code avec une application d'authentification (Google Authenticator, Aegis...).",
    mfa_setup_manual_hint:          'Ou saisissez ce code manuellement :',
    mfa_code_label:                 'Code de vérification',
    mfa_code_placeholder:           '000000',
    mfa_confirm_cta:                'Confirmer',
    mfa_verifying:                  'Vérification...',
    mfa_loading_setup:              'Génération du QR code...',
    mfa_recovery_codes_title:       'Codes de récupération',
    mfa_recovery_codes_intro:       "Conservez ces codes en lieu sûr : chacun ne peut être utilisé qu'une seule fois si vous perdez l'accès à votre application d'authentification.",
    mfa_recovery_codes_done_cta:    "J'ai noté mes codes",
    mfa_disable_password_label:     'Confirmez votre mot de passe',
    mfa_error_invalid_code:         'Code invalide',
    mfa_success_enabled:            '✅ Authentification à deux facteurs activée',
    mfa_success_disabled:           'Authentification à deux facteurs désactivée',

    // ── Profil ──
    profile_title:                  'Profil',
    profile_display_name:           'Nom d\'affichage',
    profile_name_hint:              'Affiché dans la sidebar et les exports',
    profile_save_name:              'Enregistrer le nom',
    profile_email_section:          'Adresse email',
    profile_email_locked:           "Votre adresse email identifie votre compte et ne peut pas être modifiée ici. Contactez votre administrateur si elle doit changer.",
    profile_new_email:              'Nouvel email',
    profile_current_pw:             'Mot de passe actuel',
    profile_confirm_pw_label:       'Confirmer avec votre mot de passe',
    profile_change_email:           'Changer l\'email',
    profile_pw_section:             'Mot de passe',
    profile_old_pw:                 'Mot de passe actuel',
    profile_new_pw:                 'Nouveau mot de passe',
    profile_confirm_pw:             'Confirmer le nouveau',
    profile_change_pw:              '🔒 Changer le mot de passe',
    profile_quota:                  'Quota d\'analyses',
    profile_unlimited:              '∞ Quota illimité',
    // ⚠️ Ces deux clés se choisissent sur `tier_projet_disponible`, PAS sur le
    // code du forfait — leurs noms datent de la grille PRO/PRO+. Depuis la
    // grille du 05/09/2026, l'OCR est inclus sur les TROIS paliers : seul le
    // mode Projets sépare Essentiel de Pro et Cabinet.
    profile_tier_pro_note:          "OCR inclus. Le mode Projets n'est pas compris dans ce forfait.",
    profile_tier_proplus_note:      "Mode Projets et OCR inclus. Analyses supplémentaires facturées à l'unité.",
    profile_forfait_restantes:      'analyses restantes ce mois-ci',
    profile_forfait_hors:           'analyse(s) au-delà du forfait ce mois-ci',
    profile_forfait_unite:          "l'unité",
    profile_upgrade_cta:            'Passer au plan supérieur',

    // ── Préférences ──
    pref_title:                     'Préférences',
    pref_theme:                     'Thème',
    pref_light:                     'Clair',
    pref_light_sub:                 'Thème par défaut',
    pref_dark:                      'Sombre',
    pref_dark_sub:                  'Réduit la fatigue visuelle',
    pref_system:                    'Système',
    pref_system_sub:                'Suit les paramètres de l\'OS',
    pref_lang:                      'Langue',
    pref_fr_sub:                    'Langue par défaut',
    pref_en_sub:                    'Interface anglaise',
    pref_save:                      'Enregistrer',
    pref_cancel:                    'Annuler',
    // E-mails facultatifs (révision 0022). ⚠️ Seuls les e-mails NON obligatoires se coupent.
    pref_emails:                    'E-mails',
    pref_emails_consommation:       'Alertes de consommation',
    pref_emails_consommation_sub:   'Forfait dépassé, analyses de la licence utilisées à 80 % et 100 %',
    pref_emails_obligatoires:       'Les e-mails liés à votre contrat restent envoyés : confirmation de souscription, de changement ou de résiliation, nouvelle version des conditions générales, mot de passe et invitations.',
    pref_emails_enregistre:         'Préférence enregistrée',
    pref_email_fin_analyse:         "Fin d'analyse",
    pref_email_fin_analyse_sub:     "Un e-mail quand une analyse est terminée ou n'a pas pu aboutir — utile si vous fermez l'onglet",
    pref_format_sortie:             'Format de sortie par défaut',
    pref_format_sortie_sub:         'Présélectionné à chaque dépôt ; vous pouvez toujours le changer pour un rapport.',

    // ── Page Paramètres ──
    settings_title:                 'Paramètres',
    settings_tab_profile:           'Profil',
    settings_tab_security:          'Sécurité',
    settings_tab_billing:           'Abonnement',
    settings_tab_preferences:       'Préférences',
    settings_tab_privacy:           'Mes données',

    // ── Mes données — RGPD Art. 20 et Art. 17 (5 août 2026) ──
    // Les routes existaient depuis avril, sans aucune interface : elles vivaient
    // dans `Account.js`, page jamais routée. Vocabulaire volontairement non
    // juridique — le public est métier, pas juriste.
    rgpd_export_title:              'Récupérer mes données',
    rgpd_export_intro:              'Téléchargez une archive contenant vos informations de compte, vos analyses, vos projets et les documents que vous avez déposés.',
    rgpd_export_note:               'Les rapports produits par Normascan ne sont pas inclus : vous pouvez les télécharger depuis vos analyses.',
    rgpd_export_cta:                'Télécharger mes données',
    rgpd_export_loading:            'Préparation de l\'archive…',
    rgpd_export_done:               'Archive téléchargée.',
    rgpd_delete_title:              'Supprimer mon compte',
    rgpd_delete_warning:            'Cette action est définitive. Vos analyses, vos projets, les documents déposés et vos résultats seront effacés sans possibilité de retour.',
    rgpd_delete_confirm_label:      'Saisissez',
    rgpd_delete_confirm_phrase:     'SUPPRIMER MON COMPTE',
    rgpd_delete_confirm_hint:       'pour confirmer',
    rgpd_delete_cta:                'Supprimer définitivement mon compte',
    rgpd_delete_loading:            'Suppression en cours…',
    // Parcours en 3 étapes (5 août 2026). Chaque étape apporte une information
    // NOUVELLE : trois fois « êtes-vous sûr ? » n'entraîne que le réflexe de cliquer.
    rgpd_delete_step1_cta:          'Supprimer mon compte',
    rgpd_delete_step1_question:     'Voulez-vous vraiment supprimer votre compte ?',
    rgpd_delete_step1_continue:     'Oui, continuer',
    rgpd_delete_step2_title:        'Voici ce qui sera définitivement effacé',
    rgpd_delete_step2_analyses:     'analyses',
    rgpd_delete_step2_projets:      'projets',
    rgpd_delete_step2_documents:    'documents déposés',
    rgpd_delete_step2_annotations:  'annotations',
    rgpd_delete_step2_notes:        'évaluations',
    rgpd_delete_step2_retours:      'signalements « cet avis est faux »',
    rgpd_delete_step2_files:        'Les fichiers PDF correspondants seront supprimés des serveurs.',
    rgpd_delete_step2_irreversible: 'Cette action est irréversible : rien ne pourra être restauré, ni par vous, ni par Evalens.',
    rgpd_delete_step2_export_hint:  'Vous n\'avez pas encore téléchargé vos données. Il sera trop tard ensuite.',
    rgpd_delete_step2_continue:     'J\'ai compris, continuer',
    rgpd_delete_step3_title:        'Dernière confirmation',
    rgpd_delete_step3_question:     'Êtes-vous certain de vouloir supprimer toutes vos données ? Cette action est irréversible.',
    rgpd_delete_back:               'Revenir en arrière',
    rgpd_delete_licence_title:      'Suppression du compte',
    rgpd_delete_licence_intro:      'Votre compte est rattaché à une licence entreprise. Sa suppression relève de l\'administrateur de votre licence : adressez-lui votre demande.',
    // ⚠️ Une PROPOSITION, jamais une condition. Beaucoup de gens qui cliquent
    // « supprimer mon compte » veulent seulement arrêter d'être prélevés — et
    // une résiliation se reprend d'un clic, un effacement jamais. Le bouton de
    // suppression reste accessible juste en dessous.
    rgpd_delete_sub_offer_title:    'Vous souhaitez seulement arrêter les prélèvements ?',
    rgpd_delete_sub_offer_body:     'Résilier votre abonnement suffit : vous conservez votre compte, vos rapports et votre historique, et plus rien ne vous sera prélevé. Vous pourrez revenir sur cette décision à tout moment.',
    rgpd_delete_sub_offer_cta:      'Résilier mon abonnement',
    rgpd_delete_sub_cancelled_title:'Votre abonnement sera résilié immédiatement',
    rgpd_delete_sub_cancelled_body: 'Plus aucun prélèvement n\'aura lieu. Le temps restant de la période déjà réglée n\'est pas remboursé.',
    rgpd_delete_seats_lost_singular:'collègue perdra également l\'accès à Normascan : votre licence Cabinet sera fermée. Son compte et ses analyses, eux, sont conservés.',
    rgpd_delete_seats_lost_plural:  'collègues perdront également l\'accès à Normascan : votre licence Cabinet sera fermée. Leurs comptes et leurs analyses, eux, sont conservés.',
    rgpd_contact_note:              'Pour toute question sur vos données : privacy@evalens.fr',
  },

  en: {
    // ── Login ──
    login_no_account_prompt:        'No account yet?',
    login_signup_link:              'Create an account',
    login_mfa_verify_title:         'Two-step verification',
    login_mfa_verify_intro:         'Enter the code from your authenticator app.',
    login_mfa_recovery_hint:        'You can also enter a recovery code.',
    login_mfa_back_to_login:        'Back to login',
    login_mfa_setup_required_title: 'Enhanced security required',
    login_mfa_setup_required_intro: 'Your role requires two-factor authentication to be enabled before continuing.',

    // ── Terms acceptance (24 September 2026) ──
    // ⚠️ `{v}` and `{date}` are substituted in AcceptationCgv.js.
    cgv_acc_titre:            'Terms of Use and Sale',
    cgv_acc_intro:            'To use Normascan, please accept version {v} of our Terms, in force since {date}.',
    cgv_acc_intro_a_venir:    'Version {v}, applicable from {date}, is shown to you now: by accepting, you accept both.',
    cgv_acc_ce_qui_change:    'What changes:',
    cgv_acc_lire_cgv:         'Read the Terms',
    cgv_acc_lire_privacy:     'Read the privacy policy',
    cgv_acc_aide_decision:    'Normascan is a decision-support tool: its results must be validated by a certified professional.',
    cgv_acc_case:             'I have read and accept the Terms and the privacy policy',
    cgv_acc_accepter:         'Accept and continue',
    cgv_acc_en_cours:         'Validating…',
    cgv_acc_refuser_resilier: 'I decline and cancel my subscription',
    cgv_acc_refuser:          'I decline',
    cgv_acc_refus_titre:      'You have not accepted the terms',
    cgv_acc_refus_siege:      'Without this acceptance, you cannot use Normascan. Please contact your license administrator.',
    cgv_acc_refus_individuel: 'Without this acceptance, you cannot use Normascan. For any question: contact@evalens.fr.',
    cgv_acc_erreur_portail:   'The subscription management page could not open. To cancel, write to us at contact@evalens.fr.',
    cgv_acc_deconnecter:      'Sign out',
    // Banner of the /cgv page (notice period, reading another version).
    cgv_page_a_venir:          'A new version ({v}) of these terms will apply from {date}.',
    cgv_page_lire:             'Read version {v}',
    cgv_page_lecture_a_venir:  'You are reading version {v}, which will apply from {date}.',
    cgv_page_lecture_ancienne: 'You are reading version {v}, in force from {date}, which is no longer the current one.',
    cgv_page_lire_en_vigueur:  'Read the current version ({v})',
    cgv_page_hors_ligne:       'Version {v} is no longer displayed online; the current version is shown below.',
    cgv_page_telecharger_pdf:  'Download version {v} (PDF)',
    cgv_page_sans_pdf:         'Version {v} is not available online; the current version is shown below. To obtain it: contact@evalens.fr.',
    cgv_page_inconnue:         'There is no version {v} of these terms; the current version is shown below.',
    cgv_page_archive_titre:    'Versions in PDF',
    cgv_page_archive_ligne:    'Version {v}, in force from {date} (PDF)',

    // ── Forgotten password (29 July 2026) ──
    pwd_forgot_link:                'Forgot your password?',
    pwd_forgot_title:               'Reset your password',
    pwd_forgot_intro:               'Enter your email address. If an account exists for it, you will receive a link to choose a new password.',
    pwd_forgot_email_label:         'Email address',
    pwd_forgot_submit:              'Send the link',
    pwd_forgot_sending:             'Sending…',
    pwd_forgot_sent_title:          'Check your inbox',
    pwd_forgot_sent_body:           'If an account exists for this address, a reset link has just been sent. It is valid for one hour and can only be used once.',
    pwd_back_to_login:              'Back to login',
    pwd_reset_title:                'Choose a new password',
    pwd_reset_password_label:       'New password',
    pwd_reset_confirm_label:        'Confirm password',
    pwd_reset_submit:               'Save new password',
    pwd_reset_submitting:           'Saving…',
    pwd_reset_done_title:           'Password changed',
    pwd_reset_done_body:            'Your password has been updated. You can now sign in.',
    pwd_rules:                      'At least 8 characters, one uppercase letter and one digit.',
    pwd_mismatch:                   'Passwords do not match',
    pwd_error_generic:              'An error occurred, please try again',

    // ── Sign-up closed (SIGNUP_ENABLED flag) ──
    signup_closed_contact:          'Online sign-up is temporarily closed. Contact us to open an account.',
    signup_closed_title:            'Sign-up temporarily closed',
    signup_closed_body:             'Online account creation is unavailable at the moment. Contact us to open access for your team.',
    signup_closed_login:            'I already have an account',
    signup_closed_contact_cta:      'Contact us',

    // ── Two-factor authentication ──
    mfa_section_title:              'Two-factor authentication',
    mfa_status_enabled:             'Enabled',
    mfa_status_disabled:            'Disabled',
    mfa_enable_cta:                 'Enable',
    mfa_disable_cta:                'Disable',
    mfa_setup_intro:                'Scan this QR code with an authenticator app (Google Authenticator, Aegis...).',
    mfa_setup_manual_hint:          'Or enter this code manually:',
    mfa_code_label:                 'Verification code',
    mfa_code_placeholder:           '000000',
    mfa_confirm_cta:                'Confirm',
    mfa_verifying:                  'Verifying...',
    mfa_loading_setup:              'Generating QR code...',
    mfa_recovery_codes_title:       'Recovery codes',
    mfa_recovery_codes_intro:       'Keep these codes somewhere safe: each one can only be used once if you lose access to your authenticator app.',
    mfa_recovery_codes_done_cta:    "I've saved my codes",
    mfa_disable_password_label:     'Confirm your password',
    mfa_error_invalid_code:         'Invalid code',
    mfa_success_enabled:            '✅ Two-factor authentication enabled',
    mfa_success_disabled:           'Two-factor authentication disabled',

    // ── Profile ──
    profile_title:                  'Profile',
    profile_display_name:           'Display name',
    profile_name_hint:              'Shown in sidebar and exports',
    profile_save_name:              'Save name',
    profile_email_section:          'Email address',
    profile_email_locked:           'Your email address identifies your account and cannot be changed here. Contact your administrator if it needs to change.',
    profile_new_email:              'New email',
    profile_current_pw:             'Current password',
    profile_confirm_pw_label:       'Confirm with your password',
    profile_change_email:           'Change email',
    profile_pw_section:             'Password',
    profile_old_pw:                 'Current password',
    profile_new_pw:                 'New password',
    profile_confirm_pw:             'Confirm new password',
    profile_change_pw:              '🔒 Change password',
    profile_quota:                  'Analysis quota',
    profile_unlimited:              '∞ Unlimited quota',
    profile_tier_pro_note:          'OCR included. Project mode is not part of this plan.',
    profile_tier_proplus_note:      'Project mode and OCR included. Additional analyses billed per unit.',
    profile_forfait_restantes:      'analyses left this month',
    profile_forfait_hors:           'analysis/analyses beyond the plan this month',
    profile_forfait_unite:          'each',
    profile_upgrade_cta:            'Upgrade plan',

    // ── Preferences ──
    pref_title:                     'Preferences',
    pref_theme:                     'Theme',
    pref_light:                     'Light',
    pref_light_sub:                 'Default theme',
    pref_dark:                      'Dark',
    pref_dark_sub:                  'Reduces eye strain',
    pref_system:                    'System',
    pref_system_sub:                'Follows OS settings',
    pref_lang:                      'Language',
    pref_fr_sub:                    'Default language',
    pref_en_sub:                    'English interface',
    pref_save:                      'Save',
    pref_cancel:                    'Cancel',
    // Optional e-mails (revision 0022). ⚠️ Only NON-mandatory e-mails can be turned off.
    pref_emails:                    'E-mails',
    pref_emails_consommation:       'Usage alerts',
    pref_emails_consommation_sub:   'Plan exceeded, license analyses 80% and 100% used',
    pref_emails_obligatoires:       'E-mails related to your contract are still sent: subscription, change or cancellation confirmations, new versions of the terms, password and invitations.',
    pref_emails_enregistre:         'Preference saved',
    pref_email_fin_analyse:         'Analysis finished',
    pref_email_fin_analyse_sub:     'An e-mail when an analysis is complete or could not be completed — useful if you close the tab',
    pref_format_sortie:             'Default output format',
    pref_format_sortie_sub:         'Preselected for each upload; you can still change it for a given report.',

    // ── Settings page ──
    settings_title:                 'Settings',
    settings_tab_profile:           'Profile',
    settings_tab_security:          'Security',
    settings_tab_billing:           'Subscription',
    settings_tab_preferences:       'Preferences',
    settings_tab_privacy:           'My data',

    // ── My data — GDPR Art. 20 and Art. 17 (5 August 2026) ──
    rgpd_export_title:              'Download my data',
    rgpd_export_intro:              'Download an archive with your account details, your analyses, your projects and the documents you uploaded.',
    rgpd_export_note:               'Reports generated by Normascan are not included: you can download them from your analyses.',
    rgpd_export_cta:                'Download my data',
    rgpd_export_loading:            'Preparing the archive…',
    rgpd_export_done:               'Archive downloaded.',
    rgpd_delete_title:              'Delete my account',
    rgpd_delete_warning:            'This action is permanent. Your analyses, projects, uploaded documents and results will be erased with no way back.',
    rgpd_delete_confirm_label:      'Type',
    // ⚠️ VOLONTAIREMENT en français, y compris en anglais : `delete_account`
    // (flask_app.py) compare la saisie à ce littéral exact. La traduire ferait
    // échouer toute suppression en interface anglaise, avec un 400 incompréhensible.
    // À ne changer qu'en même temps que le serveur.
    rgpd_delete_confirm_phrase:     'SUPPRIMER MON COMPTE',
    rgpd_delete_confirm_hint:       'to confirm',
    rgpd_delete_cta:                'Permanently delete my account',
    rgpd_delete_loading:            'Deleting…',
    // 3-step flow (5 August 2026) — each step adds NEW information.
    rgpd_delete_step1_cta:          'Delete my account',
    rgpd_delete_step1_question:     'Do you really want to delete your account?',
    rgpd_delete_step1_continue:     'Yes, continue',
    rgpd_delete_step2_title:        'Here is what will be permanently erased',
    rgpd_delete_step2_analyses:     'analyses',
    rgpd_delete_step2_projets:      'projects',
    rgpd_delete_step2_documents:    'uploaded documents',
    rgpd_delete_step2_annotations:  'annotations',
    rgpd_delete_step2_notes:        'ratings',
    rgpd_delete_step2_retours:      '“this opinion is wrong” reports',
    rgpd_delete_step2_files:        'The matching PDF files will be removed from the servers.',
    rgpd_delete_step2_irreversible: 'This action is irreversible: nothing can be restored, neither by you nor by Evalens.',
    rgpd_delete_step2_export_hint:  'You have not downloaded your data yet. It will be too late afterwards.',
    rgpd_delete_step2_continue:     'I understand, continue',
    rgpd_delete_step3_title:        'Final confirmation',
    rgpd_delete_step3_question:     'Are you certain you want to delete all your data? This action is irreversible.',
    rgpd_delete_back:               'Go back',
    rgpd_delete_licence_title:      'Account deletion',
    rgpd_delete_licence_intro:      'Your account belongs to a company licence. Deleting it is up to your licence administrator: please contact them.',
    rgpd_delete_sub_offer_title:    'Do you only want to stop being charged?',
    rgpd_delete_sub_offer_body:     'Cancelling your subscription is enough: you keep your account, your reports and your history, and nothing further will be charged. You can reverse that decision at any time.',
    rgpd_delete_sub_offer_cta:      'Cancel my subscription',
    rgpd_delete_sub_cancelled_title:'Your subscription will be cancelled immediately',
    rgpd_delete_sub_cancelled_body: 'No further charge will be made. The remaining time on the period already paid is not refunded.',
    rgpd_delete_seats_lost_singular:'colleague will also lose access to Normascan: your Cabinet licence will be closed. Their account and analyses are kept.',
    rgpd_delete_seats_lost_plural:  'colleagues will also lose access to Normascan: your Cabinet licence will be closed. Their accounts and analyses are kept.',
    rgpd_contact_note:              'Any question about your data: privacy@evalens.fr',
  },
};

export default compte;
